class CloudMobileZoom extends HTMLElement {
  #controller;
  #dialog;
  #stage;
  #photo;
  #error;
  #items = [];
  #index = 0;
  #token = 0;
  #pointers = new Map();
  #gesture;
  #scale = 1;
  #minimum = 1;
  #maximum = 4;
  #x = 0;
  #y = 0;
  #frame = 0;
  #lock;
  #opener;
  #resizeObserver;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#dialog = this.querySelector('dialog');
    this.#stage = this.querySelector('.cloud-mobile-zoom__stage');
    this.#photo = this.querySelector('.cloud-mobile-zoom__photo');
    this.#error = this.querySelector('.cloud-mobile-zoom__error');
    this.addEventListener('click', this.#click, { signal });
    this.#dialog.addEventListener('close', this.#cleanup, { signal });
    this.#dialog.addEventListener('keydown', this.#key, { signal });
    this.#stage.addEventListener('pointerdown', this.#pointerDown, { signal });
    this.#stage.addEventListener('pointermove', this.#pointerMove, { signal });
    this.#stage.addEventListener('pointerup', this.#pointerEnd, { signal });
    this.#stage.addEventListener('pointercancel', this.#pointerEnd, { signal });
    this.#stage.addEventListener('lostpointercapture', this.#pointerEnd, { signal });
    window.addEventListener('resize', this.#resize, { signal });
    this.#resizeObserver = new ResizeObserver(this.#resize);
    this.#resizeObserver.observe(this.#stage);
  }

  disconnectedCallback() {
    if (this.#dialog?.open) this.#dialog.close();
    this.#cleanup();
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
  }

  open(mediaIndex, opener) {
    if (window.matchMedia('(min-width: 750px)').matches) return;
    const gallery = this.closest('media-gallery');
    const media = gallery?.querySelectorAll('zoom-dialog .dialog-zoomed-gallery > li');
    this.#items = Array.from(media || []).flatMap((container, index) => {
      if (!container.classList.contains('product-media-container--image')) return [];
      const image = container.querySelector('img.product-media__image');
      if (!image) return [];
      const preview = gallery.querySelectorAll('slideshow-slide')[index]?.querySelector('img.product-media__image');
      return [{
        index,
        source: image.getAttribute('data_max_resolution') || image.currentSrc || image.src,
        preview: preview?.currentSrc || preview?.src || image.currentSrc || image.src,
        width: Number(image.getAttribute('width')) || image.naturalWidth,
        height: Number(image.getAttribute('height')) || image.naturalHeight,
        alt: image.alt,
      }];
    });
    const index = this.#items.findIndex(item => item.index === Number(mediaIndex));
    if (index < 0) return;
    this.#opener = opener instanceof HTMLElement ? opener.closest('button') || opener : null;
    if (!this.#dialog.open) {
      document.querySelectorAll('cloud-mobile-zoom').forEach(viewer => {
        if (viewer !== this) viewer.close();
      });
      this.#lockPage();
      try {
        this.#dialog.showModal();
      } catch (error) {
        this.#cleanup();
        throw error;
      }
    }
    this.#show(index);
  }

  close() {
    if (this.#dialog?.open) this.#dialog.close();
  }

  #show(index) {
    if (index < 0 || index >= this.#items.length) return;
    this.#clearPointers();
    this.#index = index;
    const item = this.#items[index];
    const token = ++this.#token;
    this.#photo.alt = item.alt;
    this.#photo.hidden = false;
    this.#error.hidden = true;
    this.#photo.onload = () => {
      if (token !== this.#token) return;
      this.#photo.hidden = false;
      this.#error.hidden = true;
      if (!item.width || !item.height) {
        item.width = this.#photo.naturalWidth;
        item.height = this.#photo.naturalHeight;
        this.#layout();
      }
    };
    this.#photo.onerror = () => {
      if (token !== this.#token) return;
      this.#photo.hidden = true;
      this.#error.hidden = false;
    };
    this.#photo.src = item.preview;
    this.#layout();
    if (item.source !== item.preview) {
      const sharper = new Image();
      sharper.decoding = 'async';
      sharper.onload = () => {
        if (token === this.#token && this.#dialog.open) this.#photo.src = item.source;
      };
      sharper.src = item.source;
    }
    this.querySelector('[data-action="previous"]').disabled = index === 0;
    this.querySelector('[data-action="next"]').disabled = index === this.#items.length - 1;
    this.querySelector('.cloud-mobile-zoom__status').textContent = this.dataset.slideStatus
      .replace('__index__', String(index + 1)).replace('__length__', String(this.#items.length));
  }

  #layout() {
    const item = this.#items[this.#index];
    if (!item?.width || !item?.height || !this.#dialog.open) return;
    const { width, height } = this.#stage.getBoundingClientRect();
    if (!width || !height) return;
    this.#minimum = Math.max(width / item.width, height / item.height);
    this.#maximum = this.#minimum * 4;
    this.#scale = this.#minimum * 1.5;
    this.#x = (width - item.width * this.#scale) / 2;
    this.#y = 0;
    this.#photo.style.width = `${item.width}px`;
    this.#photo.style.height = `${item.height}px`;
    this.#render();
  }

  #render = () => {
    this.#frame = 0;
    const item = this.#items[this.#index];
    if (!item || !this.#dialog.open) return;
    const { width, height } = this.#stage.getBoundingClientRect();
    this.#scale = Math.max(this.#minimum, Math.min(this.#maximum, this.#scale));
    this.#x = Math.max(Math.min(0, width - item.width * this.#scale), Math.min(0, this.#x));
    this.#y = Math.max(Math.min(0, height - item.height * this.#scale), Math.min(0, this.#y));
    this.#photo.style.transform = `translate3d(${this.#x}px, ${this.#y}px, 0) scale(${this.#scale})`;
  };

  #schedule() {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#render);
  }

  #point(event) {
    const rect = this.#stage.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  #startGesture() {
    const points = [...this.#pointers.values()];
    if (points.length >= 2) {
      const [a, b] = points;
      const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.#gesture = {
        distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
        scale: this.#scale,
        anchorX: (middle.x - this.#x) / this.#scale,
        anchorY: (middle.y - this.#y) / this.#scale,
      };
    } else if (points.length === 1) {
      this.#gesture = { point: points[0], x: this.#x, y: this.#y };
    } else {
      this.#gesture = null;
    }
  }

  #pointerDown = (event) => {
    if (!this.#dialog.open || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    this.#pointers.set(event.pointerId, this.#point(event));
    this.#stage.setPointerCapture(event.pointerId);
    this.#startGesture();
  };

  #pointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId) || !this.#gesture) return;
    event.preventDefault();
    this.#pointers.set(event.pointerId, this.#point(event));
    const points = [...this.#pointers.values()];
    if (points.length >= 2 && this.#gesture.distance) {
      const [a, b] = points;
      this.#scale = Math.max(this.#minimum, Math.min(this.#maximum,
        this.#gesture.scale * Math.hypot(a.x - b.x, a.y - b.y) / this.#gesture.distance));
      this.#x = (a.x + b.x) / 2 - this.#gesture.anchorX * this.#scale;
      this.#y = (a.y + b.y) / 2 - this.#gesture.anchorY * this.#scale;
    } else if (points.length === 1 && this.#gesture.point) {
      this.#x = this.#gesture.x + points[0].x - this.#gesture.point.x;
      this.#y = this.#gesture.y + points[0].y - this.#gesture.point.y;
    }
    this.#schedule();
  };

  #pointerEnd = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#render();
    }
    this.#pointers.delete(event.pointerId);
    if (this.#stage.hasPointerCapture(event.pointerId)) this.#stage.releasePointerCapture(event.pointerId);
    this.#startGesture();
  };

  #clearPointers() {
    const ids = [...this.#pointers.keys()];
    this.#pointers.clear();
    this.#gesture = null;
    for (const id of ids) {
      if (this.#stage.hasPointerCapture(id)) this.#stage.releasePointerCapture(id);
    }
  }

  #click = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!button || button.disabled) return;
    if (button.dataset.action === 'close') this.close();
    if (button.dataset.action === 'previous') this.#show(this.#index - 1);
    if (button.dataset.action === 'next') this.#show(this.#index + 1);
  };

  #key = (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.#show(this.#index + (event.key === 'ArrowRight' ? 1 : -1));
    }
  };

  #resize = () => {
    if (!this.#dialog?.open) return;
    if (window.matchMedia('(min-width: 750px)').matches) return this.close();
    this.#clearPointers();
    this.#layout();
  };

  #lockPage() {
    const properties = ['position', 'top', 'left', 'right', 'width', 'overflow'];
    this.#lock = {
      x: window.scrollX,
      y: window.scrollY,
      body: properties.map(property => [property, document.body.style.getPropertyValue(property), document.body.style.getPropertyPriority(property)]),
      overflow: document.documentElement.style.getPropertyValue('overflow'),
      priority: document.documentElement.style.getPropertyPriority('overflow'),
    };
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    for (const [property, value] of [['position', 'fixed'], ['top', `${-this.#lock.y}px`], ['left', '0'], ['right', '0'], ['width', '100%'], ['overflow', 'hidden']]) {
      document.body.style.setProperty(property, value, 'important');
    }
  }

  #cleanup = () => {
    ++this.#token;
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#clearPointers();
    if (!this.#lock) return;
    const lock = this.#lock;
    this.#lock = null;
    for (const [property, value, priority] of lock.body) {
      if (value) document.body.style.setProperty(property, value, priority);
      else document.body.style.removeProperty(property);
    }
    if (lock.overflow) document.documentElement.style.setProperty('overflow', lock.overflow, lock.priority);
    else document.documentElement.style.removeProperty('overflow');
    window.scrollTo({ left: lock.x, top: lock.y, behavior: 'instant' });
    const gallery = this.closest('media-gallery');
    const selected = this.#items[this.#index];
    if (gallery?.isConnected && selected) gallery.slideshow?.select(selected.index, undefined, { animate: false });
    if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
  };
}

if (!customElements.get('cloud-mobile-zoom')) customElements.define('cloud-mobile-zoom', CloudMobileZoom);
